import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import {
  Calendar3,
  GraphUpArrow,
  LockFill,
  Person,
  PlayBtn,
  ShieldCheck,
} from 'react-bootstrap-icons';
import Logo from '../../images/SKIP.svg';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { API_ROUTES } from '../../config/api-routes';
import { validationMessages } from '../../config/validationMessages';
import './login.css';
import Register from '../Register';
import ModalWindow from '../modal';
import axiosInstance, { startRefreshTimer } from '../../api/api';
import ForgotPassword from '../ForgotPassword';
import { toastErrorOnce, toastInfoOnce, toastSuccessOnce } from '../../utils/toastNotify';
import { useUserStore } from '../../store/userStore';
import { getSessionStore } from '../../store/sessionStore';
import { resolvePostLoginRoute, resolvePromptCount } from '../../utils/userProfile';
import { getUserFacingError } from '../../utils/userFacingError';
import OAuthButtons from '../OAuthButtons';
import TurnstileField from '../TurnstileField';
import { useTurnstileWidget } from '../../hooks/useTurnstileWidget';
import AuthLegalNotice from '../Legal/AuthLegalNotice';

const { USERNAME_PASSWORD_CANT_BE_EMPTY, LOGIN_FAILED } = validationMessages;

const VALUE_PROPS = [
  {
    icon: ShieldCheck,
    title: 'Showcase once, get seen by vetted recruiters',
    text: 'Your profile is reviewed by verified recruiters and employers.',
  },
  {
    icon: Person,
    title: 'Stand out with your portfolio',
    text: 'Highlight your skills, projects, and achievements in one powerful profile.',
  },
  {
    icon: PlayBtn,
    title: 'Skip repetitive interviews',
    text: 'Answer key questions with short videos and get straight to meaningful interviews.',
  },
  {
    icon: Calendar3,
    title: 'Share your availability',
    text: 'Let recruiters book time that works for you instantly.',
  },
  {
    icon: GraphUpArrow,
    title: 'Get insight into recruiter demand',
    text: 'Discover trends and improve your profile based on real recruiter behavior.',
  },
] as const;

const Login = () => {
  const setUser = useUserStore((state) => state.setUser);
  const setTokens = useUserStore((state) => state.setTokens);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState<React.ReactNode | null>(null);
  const [email, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState(false);
  const [verificationError, setVerificationError] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const {
    enforced: turnstileEnforced,
    token: turnstileToken,
    error: captchaError,
    setError: setCaptchaError,
    retry: retryCaptcha,
    resetWidget: resetCaptcha,
  } = useTurnstileWidget('candidate-login-turnstile-container');

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const authMessage = sessionStorage.getItem('authRedirectMessage');
    if (authMessage) {
      sessionStorage.removeItem('authRedirectMessage');
      toastInfoOnce('auth-redirect-message', authMessage);
    }

    const p = new URLSearchParams(location.search);
    const oauthError = p.get('oauth_error');
    if (oauthError) {
      toastErrorOnce('oauth-error', decodeURIComponent(oauthError));
      navigate('/', { replace: true });
      return;
    }
    const v = p.get('verified');

    if (v === '1') {
      toastSuccessOnce('email-verified', 'Verification successful. Please log in.');
      navigate('/', { replace: true });
    } else if (v === '0') {
      toastErrorOnce('email-verification-failed', 'Verification failed. Please try again.');
      navigate('/', { replace: true });
    }
  }, [location.search, navigate]);

  const handleModalClose = () => {
    setShowModal(false);
  };

  const openRegisterModal = () => {
    setModalContent(<Register setShowModal={setShowModal} onClose={handleModalClose} />);
    setShowModal(true);
  };

  const openForgotPasswordModal = () => {
    setModalContent(<ForgotPassword setShowModal={setShowModal} />);
    setShowModal(true);
  };

  const handleLogin = async (event: any) => {
    event.preventDefault();
    setDisabled(true);
    setMessage('');
    setErrorMessage(false);
    setVerificationError(false);

    if (!email || !password) {
      setMessage(USERNAME_PASSWORD_CANT_BE_EMPTY);
      setDisabled(false);
      return;
    }
    if (turnstileEnforced && !turnstileToken) {
      setCaptchaError('Please complete the CAPTCHA challenge.');
      setErrorMessage(true);
      setDisabled(false);
      return;
    }
    try {
      const usertype = 'candidate';
      const raw = {
        email,
        password,
        usertype,
        turnstileToken: turnstileEnforced ? turnstileToken : undefined,
      };
      console.log('Logging in with', raw);
      const response = await axiosInstance.post(API_ROUTES.AUTH.LOGIN, raw, {
        suppressErrorToast: true,
      });

      const data = response.data;
      if (response && response.status === 200) {
        // Store complete user data in Zustand
        const loginUser = {
          ...data.user,
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
        };
        setUser(loginUser);
        getSessionStore().seedFromLogin(loginUser);
        setTokens({
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
        });

        // Keep these for backward compatibility
        localStorage.setItem('accessToken', data.access_token);
        localStorage.setItem('refreshToken', data.refresh_token);
        localStorage.setItem('user', data.user);

        startRefreshTimer();
        const session = await getSessionStore().bootstrapSession({
          force: true,
        });
        const promptCount = resolvePromptCount(session.prompts ?? []);
        const userRecord = session.user as Record<string, unknown> | null;

        setMessage('Login successful');
        setErrorMessage(false);
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'conversion', {
            send_to: 'AW-17935827664/v-x6CNDL7_kbENCFvOhC',
          });
        }
        navigate(resolvePostLoginRoute(userRecord, promptCount));
      }
    } catch (error: unknown) {
      resetCaptcha();
      const status = (error as { response?: { status?: number } })?.response?.status;
      const err = getUserFacingError(error, LOGIN_FAILED);

      if (
        (status === 400 || status === 401 || status === 403) &&
        err.includes('Verification Failed')
      ) {
        setVerificationError(true);
        setMessage('Please verify your email first!');
      } else if (status === 403 && err.includes('deactivated')) {
        setErrorMessage(true);
        setMessage('This account has been deactivated. Contact customer support.');
      } else {
        setErrorMessage(true);
        setMessage(err);
      }
      setDisabled(false);
    }
  };

  return (
    <div className="login-main-wrapper">
      <div className="login-bg-image" />
      <div className="login-bg-overlay" />

      <main className="login-content-grid">
        <section className="login-value-section" aria-label="Candidate value proposition">
          <div className="login-value-list">
            {VALUE_PROPS.map((value) => {
              const Icon = value.icon;
              return (
                <article className="login-value-card" key={value.title}>
                  <span className="login-value-card__icon" aria-hidden>
                    <Icon size={22} />
                  </span>
                  <div className="login-value-card__body">
                    <h3>{value.title}</h3>
                    <p>{value.text}</p>
                  </div>
                </article>
              );
            })}
          </div>

          <footer className="login-page-footer">
            <p className="login-page-footer__copy">© 2026 SkipScreen LLC</p>
            <nav className="login-page-footer__links" aria-label="Legal links">
              <Link to="/terms-of-service">Terms of Service</Link>
              <Link to="/privacy-policy">Privacy Policy</Link>
              <Link to="/cookie-policy">Cookie Policy</Link>
              <Link to="/community-standards">Community Standards</Link>
              <a href="mailto:support@useskipscreen.com">Contact Us</a>
            </nav>
            <p className="login-page-footer__secure">
              <LockFill size={14} aria-hidden />
              <span>
                Your data is encrypted and never sold. You control who can view your profile and can
                revoke recruiter access at any time.
              </span>
            </p>
          </footer>
        </section>

        <section className="login-form-panel" aria-label="Candidate login form">
          <img className="logo-img" alt="Logo" src={Logo} />
          <h2 className="card-title">LOG IN</h2>

          <form className="login-form" onSubmit={handleLogin}>
            <div className="form-group mb-3">
              {message && (
                <div
                  className={`alert ${errorMessage || verificationError ? 'alert-danger' : 'alert-success'}`}
                  role="alert"
                >
                  {message}
                </div>
              )}

              <label htmlFor="username">Email:</label>
              <input
                type="email"
                className="form-control login-input"
                id="username"
                name="username"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>

            <div className="form-group mb-3">
              <label htmlFor="password">Password:</label>

              <div className="input-group login-password-group">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-control login-input"
                  id="password"
                  name="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />

                <div className="input-group-append login-eye-btn-wrap">
                  <button
                    type="button"
                    className="btn btn-outline-secondary border-left-0 login-eye-btn"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                  </button>
                </div>
              </div>
            </div>
            {turnstileEnforced && (
              <TurnstileField
                containerId="candidate-login-turnstile-container"
                error={captchaError}
                onRetry={retryCaptcha}
              />
            )}

            <Button
              variant="success"
              type="submit"
              className="submit-btn-login login-form-button"
              disabled={disabled || (turnstileEnforced && !turnstileToken)}
            >
              Sign in
            </Button>

            <div className="btn-box">
              <button type="button" className="login-to-register-btn" onClick={openRegisterModal}>
                Sign up now!
              </button>
              <button
                type="button"
                className="login-to-register-btn"
                onClick={openForgotPasswordModal}
              >
                Forgot password?
              </button>
            </div>

            <OAuthButtons layout="stacked" mode="login" />
            <AuthLegalNotice action="signing in" className="login-legal-footer" />
          </form>
        </section>
      </main>
      {showModal && (
        <ModalWindow showModal={showModal} handleModalClose={handleModalClose}>
          {modalContent}
        </ModalWindow>
      )}
    </div>
  );
};

export default Login;
